import { useCompanyClasses } from "@vidario/api-client";
import { Button, EmptyState, Spinner, Tabs } from "@vidario/ui";
import { useState, type ReactElement } from "react";
import { useSearchParams } from "react-router";

import { useAnonymView } from "../../anonym/useAnonymView";
import { useIsAdmin } from "../../guards/useRole";
import { ClassesTab } from "./ClassesTab";
import { UnassignedTab } from "./UnassignedTab";

const TAB_TODO = "offen";
const TAB_CLASSES = "klassen";

/** Firmenklassen (#139, admin-only): which employers belong to which class.
 * The Anonym-Ansicht (#140) shows the class instead of the employer's name. */
export function CompanyClassesRoute(): ReactElement {
  const isAdmin = useIsAdmin();
  const anonym = useAnonymView();
  if (!isAdmin) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
        <EmptyState
          title="Kein Zugriff"
          description="Firmenklassen sind nur für Administratoren sichtbar."
        />
      </main>
    );
  }
  if (anonym) {
    // The page lists employer names in clear text (#139/#140).
    return (
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
        <EmptyState
          title="In der Anonym-Ansicht gesperrt"
          description="Diese Seite zeigt Arbeitgebernamen im Klartext. Schalte die Anonym-Ansicht aus, um Firmenklassen zu bearbeiten."
        />
      </main>
    );
  }
  return <CompanyClassesScreen />;
}

function CompanyClassesScreen(): ReactElement {
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isFetching, error, refetch } = useCompanyClasses();
  // One polite status region for every change on the page. A change is
  // announced once the counts have refreshed, so „… offen" is the new number.
  const [announcement, setAnnouncement] = useState("");
  const [pendingText, setPendingText] = useState<string | null>(null);
  if (pendingText !== null && data && !isFetching) {
    setAnnouncement(`${pendingText} ${data.unassignedCount.toLocaleString("de-DE")} offen.`);
    setPendingText(null);
  }

  const todo = data?.unassignedCount ?? 0;
  // The to-do list opens first while it has entries.
  const tab = params.get("tab") ?? (data && todo === 0 ? TAB_CLASSES : TAB_TODO);
  const announce = (text: string): void => setPendingText(text);

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 pb-20 sm:px-8">
      <header className="mb-4">
        <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">Firmenklassen</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Ordne Arbeitgeber einer Klasse zu. In der Anonym-Ansicht erscheint die Klasse statt des
          Firmennamens.
        </p>
      </header>

      <p role="status" className="sr-only">
        {announcement}
      </p>

      {isLoading && (
        <div className="flex items-center gap-2 px-1 py-6 text-sm text-text-secondary">
          <Spinner size={18} />
          <span>Lädt</span>
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-center gap-3 px-1 py-6 text-sm text-danger-600">
          Firmenklassen konnten nicht geladen werden.
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            Erneut versuchen
          </Button>
        </div>
      )}

      {data && (
        <Tabs
          aria-label="Firmenklassen"
          value={tab}
          onValueChange={(value) =>
            setParams((current) => {
              const next = new URLSearchParams();
              next.set("tab", value);
              if (value === TAB_CLASSES && current.get("klasse"))
                next.set("klasse", current.get("klasse") ?? "");
              return next;
            })
          }
          items={[
            {
              value: TAB_TODO,
              label: `Nicht zugeordnet (${todo})`,
              content: <UnassignedTab classes={data.classes} announce={announce} />,
            },
            {
              value: TAB_CLASSES,
              label: `Klassen (${data.classes.length})`,
              content: <ClassesTab classes={data.classes} announce={announce} />,
            },
          ]}
        />
      )}
    </main>
  );
}
